import * as yup from 'yup'

import { VALIDATION } from '../constants.js'

yup.setLocale({
  mixed: {
    required: VALIDATION.MESSAGES.REQUIRED
  },
  string: {
    required: VALIDATION.MESSAGES.REQUIRED,
    email: VALIDATION.MESSAGES.EMAIL
  },
  number: {
    integer: VALIDATION.MESSAGES.INTEGER,
    positive: VALIDATION.MESSAGES.POSITIVE
  }
})

yup.addMethod(yup.number, 'addsUpTo100', function (key) {
  return this.test('100-percent', VALIDATION.MESSAGES.MUST_ADD_UP_TO_100_PERCENT(key), (value, context) => {
    if (value === undefined || value === null) return true
    if (context.parent[key] === undefined || context.parent[key] === null) return false
    return (value + context.parent[key]) === 100
  })
})

yup.addMethod(yup.mixed, 'existsUnless', function (key, options) {
  return this.existsWhen(key, value => value === null || value === undefined, options)
})

yup.addMethod(yup.mixed, 'existsWhen', function (key, when, { defined = true } = {}) {
  function exists (value) {
    if (typeof when === 'function') return !!when(value)
    else return value === when
  }

  return this.when(key, (value, schema) => {
    if (exists(value)) return schema
    else if (defined && value !== undefined) return yup.mixed().oneOf([null], VALIDATION.MESSAGES.MUST_BE_NULL).defined()
    else return yup.mixed().oneOf([null], VALIDATION.MESSAGES.MUST_BE_NULL)
  })
})

yup.addMethod(yup.mixed, 'existsWhenIncludes', function (key, whenValue) {
  return this.when(key, (arr, schema) => {
    if (Array.isArray(arr) && arr.includes(whenValue)) return schema
    else if (arr !== undefined) return yup.mixed().oneOf([null], VALIDATION.MESSAGES.MUST_BE_NULL).defined()
    else return yup.mixed().oneOf([null], VALIDATION.MESSAGES.MUST_BE_NULL)
  })
})

yup.addMethod(yup.mixed, 'defaultIfRequired', function (defaultValue) {
  return this.when('.', (_, schema) => {
    if (schema.spec.presence === 'required') return schema.default(defaultValue)
    else return schema
  })
})

yup.addMethod(yup.mixed, 'pickNested', function (pathes) {
  const picked = {}

  for (const path of pathes) {
    if (typeof path === 'string') {
      picked[path] = this.fields[path]
    } else {
      for (const key in path) {
        const pickedNested = this.fields[key].clone()

        pickedNested.withMutation(() => pickedNested.pickNested(path[key]))

        picked[key] = pickedNested
      }
    }
  }

  return this.clone().withMutation((next) => {
    next.fields = {}
    return next.shape(picked)
  })
})

yup.addMethod(yup.mixed, 'omitNested', function (pathes) {
  const next = this.clone()
  const fields = next.fields
  next.fields = {}

  for (const path of pathes) {
    if (typeof path === 'string') {
      delete fields[path]
    } else {
      for (const key in path) {
        fields[key] = fields[key].clone()
        fields[key].withMutation(() => fields[key].omitNested(path[key]))
      }
    }
  }

  return next.withMutation(() => next.shape(fields))
})

yup.addMethod(yup.object, 'allFieldsRequired', function () {
  return this.shape(
    Object.fromEntries(Object.keys(this.fields).map(key => {
      const schema = this.fields[key]
      if (schema.type === 'object') return [key, schema.allFieldsRequired()]
      else return [key, schema.required()]
    }))
  )
})

yup.addMethod(yup.object, 'allFieldsOptional', function () {
  return this.shape(
    Object.fromEntries(Object.keys(this.fields).map(key => {
      const schema = this.fields[key]
      if (schema.type === 'object') return [key, schema.allFieldsOptional().objectOptional()]
      // TODO: we may need this in the future
      // if (schema.type === 'array') return [key, schema.innerTypeOptional().optional()]
      else return [key, schema.optional()]
    }))
  )
})

yup.addMethod(yup.array, 'innerTypeOptional', function () {
  const schema = this.innerType
  if (schema.type === 'object') return this.of(schema.allFieldsOptional().objectOptional())
  if (schema.type === 'array') return this.of(schema.innerTypeOptional())
  else return this.of(schema.optional())
})

yup.addMethod(yup.object, 'objectOptional', function () {
  return this.optional().default(undefined).noUnknown()
})

yup.addMethod(yup.object, 'objectRequired', function () {
  return this.noUnknown()
})

yup.addMethod(yup.object, 'get', function (path) {
  return yup.reach(this, path)
})

yup.addMethod(yup.string, 'addProtocol', function () {
  return this.transform((url) => {
    if (
      url.startsWith('http://') ||
      url.startsWith('https://') ||
      !url
    ) {
      return url
    } else {
      return 'https://' + url
    }
  })
})

yup.addMethod(yup.string, 'customUrl', function (message) {
  return this.test({
    name: 'customUrl',
    message: message,
    test: (value) => {
      if (!value) {
        return true;
      }
      try {
        const url = new URL(value);
        return ['http:', 'https:'].includes(url.protocol);
      } catch (_) {
        return false;
      }
    }
  });
});

yup.addMethod(yup.string, 'removeProtocol', function () {
  return this.transform((url) => {
    return url.replace('http://', '').replace('https://', '')
  })
})

yup.addMethod(yup.string, 'addHashtag', function () {
  return this.transform((color) => {
    if (color.startsWith('#')) {
      return color
    } else {
      return '#' + color
    }
  })
})

yup.addMethod(yup.string, 'startsWith', function (string, message = VALIDATION.MESSAGES.MUST_START_WITH) {
  return this.test({
    name: 'startsWith',
    exclusive: true,
    message,
    params: { string },
    test (value) {
      return !value || value.startsWith(string)
    }
  })
})

yup.addMethod(yup.string, 'phone', function (message = VALIDATION.MESSAGES.PHONE) {
  return this.matches(VALIDATION.REGEXP.PHONE, message)
})

yup.addMethod(yup.string, 'password', function (message = VALIDATION.MESSAGES.PASSWORD) {
  return this.matches(VALIDATION.REGEXP.PASSWORD, message)
})

yup.addMethod(yup.string, 'simplePassword', function (message = VALIDATION.MESSAGES.SIMPLE_PASSWORD) {
  return this.matches(VALIDATION.REGEXP.SIMPLE_PASSWORD, message)
})

yup.addMethod(yup.string, 'zip', function (message = VALIDATION.MESSAGES.ZIP) {
  return this.matches(VALIDATION.REGEXP.ZIP, message)
})

yup.addMethod(yup.string, 'subdomain', function (message = VALIDATION.MESSAGES.SUB_DOMAIN) {
  return this.matches(VALIDATION.REGEXP.SUB_DOMAIN, message)
})

yup.addMethod(yup.string, 'color', function (message = VALIDATION.MESSAGES.COLOR) {
  return this.matches(VALIDATION.REGEXP.COLOR, message)
})

yup.addMethod(yup.array, 'notEmpty', function () {
  return this.min(1, VALIDATION.MESSAGES.NON_EMPTY_ARRAY)
})

yup.addMethod(yup.array, 'unique', function (mapper = a => a) {
  return this.test('unique', 'Array must be unique', function (list) {
    return !list || list.length === new Set(list.map(mapper)).size
  })
})

yup.addMethod(yup.number, 'decimal', function (maxDecimal) {
  return this.test(
    'maxDigitsAfterDecimal',
    `Must have ${maxDecimal} decimal${maxDecimal > 1 ? 's' : ''} or less`,
    (value) => !value || new RegExp(`^\\d+(\\.\\d{1,${maxDecimal}})?$`).test(value)
  )
})

yup.addMethod(yup.array, 'dateRange', function (message = VALIDATION.MESSAGES.DATE) {
  return this.test('dateRange', message, function (list) {
    return list[0] <= list[1] && list[1] <= new Date()
  })
})

yup.addMethod(yup.array, 'priceInputRange', function (message = VALIDATION.MESSAGES.PRICE_INPUT_RANGE) {
  return this.test('priceInputRange', message, function (list) {
    return list[0] <= list[1]
  })
})

yup.addMethod(yup.string, 'toLowerCase', function () {
  return this.transform((value) => value.toLowerCase())
})
